/**
 * React Hook: Generate SOQL from natural language
 *
 * Uses React Query's useMutation to call POST /v2/agent/chat.
 * Automatically uses selectedOrg from auth store.
 *
 * @example
 * ```tsx
 * const { generateSoql, isPending, data, error } = useGenerateSoql();
 * await generateSoql("Get all accounts created this year");
 * // data?.query, data?.explanation, data?.error
 * ```
 */

import { useMutation } from '@tanstack/react-query';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { getOrgAbortSignal, isAbortError } from '@/lib/org-scope';
import { agentChat } from '@/services/text-to-soql';
import type { AgentChatResponse } from '@/services/text-to-soql';

const SESSION_ID = 'text-to-soql';

export function useGenerateSoql() {
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const userId = useAuthStoreV1((state) => state.user?.id);

  const mutation = useMutation<AgentChatResponse, Error, string>({
    mutationFn: async (message: string) => {
      if (!selectedOrg) {
        throw new Error('No organization selected');
      }
      return agentChat(
        { message: message.trim(), session_id: SESSION_ID },
        selectedOrg,
        userId,
        getOrgAbortSignal(),
      );
    },
    onError: (error) => {
      if (isAbortError(error)) return; // the org changed; not a failure
      console.error('❌ Failed to generate SOQL:', error);
    },
  });

  return {
    generateSoql: mutation.mutateAsync,
    isPending: mutation.isPending,
    data: mutation.data ?? null,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}
